import type { ScopedRepairStepProgress } from './mappingRepairProgress';
import type { MappingRepairVerificationResult } from './mappingRepairVerification';
import { toTraceKey, warnMappingFlow } from './mappingTrace';

type VerificationRunInput = {
  readonly projectKey: string | null;
  readonly signature: string;
  readonly verifyRepairParameters: () => Promise<MappingRepairVerificationResult>;
  readonly updateRepairStep: (status: 'current' | 'complete' | 'error') => void;
  readonly retainFailureProgress: (message: string) => void;
  readonly setRepairStepProgress: (value: ScopedRepairStepProgress | null) => void;
  readonly setRepairError: (value: { signature: string; message: string } | null) => void;
};

export async function runMappingRepairVerification(input: VerificationRunInput): Promise<string | null> {
  input.updateRepairStep('current');
  const startedAt = Date.now();
  input.setRepairStepProgress({
    stepId: 'verify',
    model: {
      title: 'Checking project parameters',
      message: 'Revit is checking project parameters after setup changes',
      detail: 'Refreshing both active-project parameter catalogs.',
      status: 'running',
      percent: null,
      locked: true,
    },
  });
  warnMappingFlow('mapping readiness repair verification start', {
    projectKey: input.projectKey,
    signatureKey: toTraceKey(input.signature),
  });

  try {
    const verification = await input.verifyRepairParameters();
    if (!verification.success) {
      input.updateRepairStep('error');
      warnMappingFlow('mapping readiness repair verification failed', {
        projectKey: input.projectKey,
        signatureKey: toTraceKey(input.signature),
        message: verification.message,
        missingParameterNames: verification.missingParameterNames.slice(0, 20),
      });
      input.retainFailureProgress(verification.message);
      input.setRepairError({ signature: input.signature, message: verification.message });
      return verification.message;
    }

    input.updateRepairStep('complete');
    input.setRepairStepProgress({
      stepId: 'verify',
      model: {
        title: 'Checking project parameters',
        message: verification.message,
        detail: 'Required mapping setup is available in the fresh combined Revit catalog.',
        status: 'complete',
        percent: 100,
        locked: true,
      },
    });
    warnMappingFlow('mapping readiness repair verification done', {
      projectKey: input.projectKey,
      signatureKey: toTraceKey(input.signature),
      elapsedMs: Date.now() - startedAt,
    });
    return null;
  } catch (error) {
    input.updateRepairStep('error');
    warnMappingFlow('mapping readiness repair verification error', {
      projectKey: input.projectKey,
      signatureKey: toTraceKey(input.signature),
      elapsedMs: Date.now() - startedAt,
      errorType: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : 'Project parameter verification failed.',
    });
    throw error;
  }
}
